import { useCallback } from 'react';

import * as crawlerApi from 'api/crawlerApi';
import { useToast } from 'hooks/useToast';
import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import {
  listSelectors,
  listActions,
} from 'store/reducers/crawlerReducer/listSlice';
import { detailActions } from 'store/reducers/crawlerReducer/detailSlice';
import type { Crawler } from 'types/crawler';

/**
 * クローラー一覧 store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useListStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const loading = useAppSelector(listSelectors.loading);
  const crawlers = useAppSelector(listSelectors.filteredCrawlers);
  const searchValue = useAppSelector(listSelectors.searchValue);

  const fetchCrawlers = useCallback(async () => {
    dispatch(listActions.loadStarted());
    const result = await crawlerApi.fetchCrawlers();
    if (result.ok) {
      dispatch(
        listActions.fetchCrawlersSuccess({
          crawlers: result.value,
        }),
      );
    } else {
      toast({
        status: 'error',
        description: `クローラー一覧の取得に失敗しました: ${result.err.message}`,
      });
    }
    dispatch(listActions.loadEnded());
  }, [dispatch, toast]);

  const changeSearchValue = useCallback(
    (value: string) => {
      dispatch(listActions.searchValueChanged({ value }));
    },
    [dispatch],
  );

  const onOpenModal = useCallback(
    (crawler: Crawler) => {
      dispatch(detailActions.modalOpened(crawler));
    },
    [dispatch],
  );

  return {
    /** 一覧ロード状態 */
    loading,
    /** クローラー一覧データ */
    crawlers,
    /** 検索文字列 */
    searchValue,
    /** クローラー一覧を取得 */
    fetchCrawlers,
    /** 検索文字を変更 */
    changeSearchValue,
    /** クローラー詳細を表示 */
    onOpenModal,
  };
};
